import { useState } from "react";
import { useApplication } from "../../ApplicationContext";
import { useLenderOperations } from "src/hooks/useLenderOperations";
import {
  Container,
  HeaderRow,
  LeftContainer,
  RightContainer,
} from "./LendersList.styles";
import LenderRow from "./LenderRow";
import { APPLICATION_STATUS_KEYS } from "src/constants";
import PrimaryBadge from "src/components/PrimaryBadge";
import type {
  DomainApplicationLenderConfiguration,
  DtoSubmitApplicationToLenderResult,
} from "@porchpass/api-client";
import { DomainLender } from "@porchpass/api-client";
import { GhostButton } from "src/components/Buttons";
import { ArrowRightIcon, DownloadIcon } from "src/assets/icons";
import { mapLenderSubmitResults } from "src/utils/mapLenderSubmitResults";
import { useFullApplicationDocument } from "src/hooks/useFullApplicationDocument";
import { toast } from "@porchpass/ui";

const EXCLUDED_SUBMIT_LENDER_NAMES = new Set([
  "Patriot Act Form",
  "Titan Credit Disclosure",
]);

export default () => {
  const [currentlySubmittedLenders, setCurrentlySubmittedLenders] = useState(
    new Set<string>(),
  );
  const [isSubmittingAll, setIsSubmittingAll] = useState(false);
  const [submitResults, setSubmitResults] = useState<
    Record<string, DtoSubmitApplicationToLenderResult>
  >({});
  const { application, refetchApplication } = useApplication();
  const { isDownloading, downloadFullApplicationDocument } =
    useFullApplicationDocument(application?.application_id || "");
  const { setPrimaryLender, submitToLender, submitToAllLenders } =
    useLenderOperations(application, refetchApplication);

  const [highlightedLender, setHighlightedLender] = useState<string | null>(
    null,
  );

  const onPrimaryLenderSet = (lender_id: string) => {
    return setPrimaryLender(lender_id)
      .then(() => {
        setHighlightedLender(lender_id);
        setTimeout(() => setHighlightedLender(null), 2000);
      })
      .catch((_error) => {
        // Error handling is done in context
      });
  };

  const sortLenders = (
    lenders: DomainApplicationLenderConfiguration[] | undefined,
  ): DomainApplicationLenderConfiguration[] => {
    if (!lenders) return [];

    return lenders.sort((a, b) => {
      const aIsPrimary = a.id === application.primary_lender;
      const bIsPrimary = b.id === application.primary_lender;

      if (aIsPrimary && !bIsPrimary) return -1;
      if (!aIsPrimary && bIsPrimary) return 1;

      if (a.enabled && !b.enabled) return -1;
      if (!a.enabled && b.enabled) return 1;

      return 0;
    });
  };

  const enabledLenders = sortLenders(application?.lenders).filter(
    (lender) => lender.enabled && !!lender.app_status,
  );

  const onLenderSubmit = (lender_id: string) => {
    setCurrentlySubmittedLenders((prev) => new Set(prev).add(lender_id));
    setSubmitResults((prev) => {
      const { [lender_id]: _removed, ...rest } = prev;
      return rest;
    });

    return submitToLender(lender_id)
      .catch((_error) => {
        // Error handling is done in context
      })
      .finally(() => {
        setCurrentlySubmittedLenders((prev) => {
          const newIds = new Set(prev);
          newIds.delete(lender_id);
          return newIds;
        });
      });
  };

  const readyToSubmitLenders = enabledLenders.filter(
    (lender) =>
      lender.app_status === APPLICATION_STATUS_KEYS["Ready to submit"],
  );

  const showSubmitAll = readyToSubmitLenders.length > 1;
  const showDownloadFullPdf = Boolean(application?.application_id);
  const showHeaderActions = showSubmitAll || showDownloadFullPdf;

  const submitAllLenders = (
    submitableLenders: DomainApplicationLenderConfiguration[],
  ) => {
    const lendersToSubmit = submitableLenders.filter(
      (lender) =>
        lender.app_status === APPLICATION_STATUS_KEYS["Ready to submit"] &&
        !EXCLUDED_SUBMIT_LENDER_NAMES.has(lender.name ?? ""),
    );

    if (lendersToSubmit.length === 0) return;

    const lenderIds = lendersToSubmit.map((lender) => lender.id ?? "");
    setIsSubmittingAll(true);
    setSubmitResults({});

    void submitToAllLenders(lenderIds as DomainLender[])
      .then((results) => {
        setSubmitResults(mapLenderSubmitResults(results));

        const porchpassResult = results.find(
          (result) => result.lender_id === DomainLender.PorchPass,
        );
        if (porchpassResult?.success === false && porchpassResult.error) {
          toast.error(porchpassResult.error);
        }
      })
      .catch((_error) => {
        // Error handling is done in useLenderOperations / toast
      })
      .finally(() => setIsSubmittingAll(false));
  };

  return (
    <Container>
      <HeaderRow>
        <LeftContainer>
          Assign a
          <PrimaryBadge />
          lender and trigger submissions manually when ready
        </LeftContainer>
        {showHeaderActions && (
          <RightContainer>
            {showDownloadFullPdf && (
              <GhostButton
                onClick={downloadFullApplicationDocument}
                loading={isDownloading}
              >
                <DownloadIcon />
                Download Full PDF
              </GhostButton>
            )}
            {showSubmitAll && (
              <GhostButton
                onClick={() => submitAllLenders(readyToSubmitLenders)}
                loading={isSubmittingAll}
              >
                Submit all
                <ArrowRightIcon />
              </GhostButton>
            )}
          </RightContainer>
        )}
      </HeaderRow>
      {enabledLenders.map((lender) => (
        <LenderRow
          key={lender.name}
          lenderId={lender.id ?? ""}
          logoUrl={lender.logo_url}
          title={lender.name ?? ""}
          appStatus={lender.app_status ?? ""}
          lenderStatus={lender.lender_status ?? ""}
          date={lender.status_updated_at}
          state={""}
          isPrimaryLender={lender.id === application.primary_lender}
          onPrimaryLenderSet={() => {
            void onPrimaryLenderSet(lender.id ?? "");
          }}
          onLenderSubmit={() => {
            void onLenderSubmit(lender.id ?? "");
          }}
          isCurrentlySubmitting={
            currentlySubmittedLenders.has(lender.id ?? "") || isSubmittingAll
          }
          isHighlighted={lender.id === highlightedLender}
          statusUpdatedAt={lender.status_updated_at}
          submitResult={submitResults[lender.id ?? ""]}
        />
      ))}
    </Container>
  );
};
